import * as React from 'react'
import { CountryOptionsListContext } from './CountryOptionsListContext'
import { LanguageOptionsListContext } from './LanguageOptionsListContext'
import { getCountriesList } from '../../services/countryService'
import { getLanguagesList } from '../../services/languagesService'
import { EvaluatorsOptionsListContext } from './EvaluatorsOptionsListContext'
import { OptionsType } from 'react-select'
import { getEvaluators } from '../../services/userService'
import { useStoreState } from '../../store'
import { IEvaluator } from '../../model/User'
import { userRole } from '../../utils/getValues'
import { hasLiveRefreshToken } from '../../services/session'
interface IGeneralOptionsProviderProps {
  children: React.ReactNode
}

const GeneralOptionsProvider = (props: IGeneralOptionsProviderProps) => {
  const [countries, setCountries] = React.useState<OptionsType>([])
  const [languages, setLanguages] = React.useState<OptionsType>([])
  const [evaluators, setEvaluators] = React.useState<IEvaluator[]>([])
  const currentUser = useStoreState((state) => state.user.current)

  React.useEffect(() => {
    ;(async () => {
      // Both endpoints are @jwt_required, and every screen that reads these lists sits
      // behind PrivateRoute -- so fetching them while signed out could only ever 401. It
      // did, on every public page load including the sign-in page, and that was the
      // largest single source of 401s against the API (~175 a day). Worse, the response
      // interceptor used to read any 401 as proof the session was finished: one of these
      // requests, issued while the sign-in page was still up, could land just after a
      // login completed and tear down the session that had replaced it.
      //
      // This provider sits above the router and re-runs when currentUser changes, so
      // signing in is what triggers the fetch now, with a token attached.
      if (!hasLiveRefreshToken()) return
      if (!countries?.length) {
        setCountries(await getCountriesList())
      }
      if (!languages?.length) {
        setLanguages(await getLanguagesList())
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  React.useEffect(() => {
    ;(async () => {
      // fetch evaluators if not client user
      // Same reasoning: a persisted currentUser can outlive its tokens, and this call is
      // authenticated too.
      if (!hasLiveRefreshToken()) return
      currentUser && !userRole.includes(currentUser.role) && setEvaluators(await getEvaluators())
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  return (
    <EvaluatorsOptionsListContext.Provider value={{ evaluators }}>
      <CountryOptionsListContext.Provider value={{ countries }}>
        <LanguageOptionsListContext.Provider value={{ languages }}>
          {props.children}
        </LanguageOptionsListContext.Provider>
      </CountryOptionsListContext.Provider>
    </EvaluatorsOptionsListContext.Provider>
  )
}

export default GeneralOptionsProvider
